/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useRef, useState } from 'react';
import { Text } from '@/shared/ui';

/** One slice: what it counts, how much, and the colour it was assigned. */
export interface Slice {
  key: string;
  label: string;
  count: number;
  colour: string;
  /**
   * The category values this slice stands for, for the list it points at.
   *
   * A list rather than one string because the summed tail is several
   * categories at once. Absent where the slice has nothing to filter on --
   * the listings with no category recorded.
   */
  filter?: string[];
}

/** Drawn in a square viewBox; the ring is sized from the box, not the data. */
const BOX = 100;
const RADIUS = 38;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * The gap between two slices, as a share of the ring.
 *
 * Two neighbouring slices need something between them that is not a third
 * colour. The ramp is ordered so touching slices clear ΔE 46, but the
 * palette is dark and low-chroma throughout and the floor across the
 * whole set is ΔE 23, so the gap still does real work -- it is what keeps
 * two wedges from reading as one when they happen to land side by side.
 */
const GAP = 1.6;

/** Where one slice starts and how long it runs, in ring units. */
function arcOf(slice: Slice, total: number, offset: number) {
  const length = (slice.count / total) * CIRCUMFERENCE;
  return {
    // The gap is taken out of the slice rather than added between them, so
    // the ring still closes and the last slice does not overrun the first.
    dash: `${Math.max(0, length - GAP)} ${CIRCUMFERENCE - Math.max(0, length - GAP)}`,
    offset: -offset,
    next: offset + length,
  };
}

/**
 * The book split by category, as a ring around its own total.
 *
 * A ring rather than a pie: the middle is where the total belongs, and a
 * reader who wants "how many altogether" should not have to add six slices
 * up. Slices are ordered biggest first from twelve o'clock, so the ranking
 * is read clockwise without consulting the legend.
 *
 * Every slice carries its own `<title>`, which is what a pointer and a
 * screen reader both read: the ring is the shape, and the words are how the
 * value is actually obtained.
 *
 * @param props.slices - The categories, already ordered and coloured
 * @param props.total - Every listing in the book, the slices' sum included
 * @param props.totalLabel - What the number in the middle counts
 */
export function CategoryDonut({
  slices, total, totalLabel, onSelect,
}: {
  slices: Slice[];
  total: number;
  totalLabel: string;
  /** Called with the wedge that was clicked; omit to leave the ring inert. */
  onSelect?: (slice: Slice) => void;
}) {
  /*
   * Which wedge the pointer is on, and where in the box it is.
   *
   * `<title>` alone was the answer before, and it is not one: the native
   * tooltip waits about a second, appears wherever the OS decides, and
   * never fires at all on touch. A reader pointing at a wedge is asking
   * "which one is this" and should be answered immediately, next to the
   * wedge. The `<title>` stays regardless -- it is what a screen reader
   * reads, and hover is not available to every reader.
   */
  const [hovered, setHovered] = useState<string | null>(null);
  const [pointer, setPointer] = useState({ x: 0, y: 0 });
  const boxRef = useRef<HTMLDivElement>(null);

  if (!total) return null;

  let cursor = 0;
  const active = slices.find((slice) => slice.key === hovered);

  /*
   * Pointer position relative to the box the label is positioned in.
   *
   * Measured off the box and not off `event.currentTarget`: the target is
   * the wedge, whose bounding box is its own arc, so offsets taken from it
   * are relative to a different origin than the one `absolute` resolves
   * against and the label drifts away from the cursor.
   */
  const track = (event: React.MouseEvent<SVGElement>) => {
    const box = boxRef.current?.getBoundingClientRect();
    if (!box) return;
    setPointer({ x: event.clientX - box.left, y: event.clientY - box.top });
  };

  return (
    /*
      * The ring takes the card's leftover height rather than a size of its
      * own.
      *
      * `flex-1` against a `min-h-0` track means it is measured from what
      * the title and the legend did not use, so it can never be the reason
      * the card grows -- and the taller the card is stretched (advanced
      * mode adds rows to the tiles beside it), the bigger the ring gets,
      * with no scroll and no second breakpoint to keep in sync.
      *
      * The floor is what the short card had, for the layouts where the
      * card is only as tall as its contents and there is no slack to take;
      * the ceiling stops a very tall column from turning a legend's worth
      * of chart into the page's centrepiece.
      */
    <div
      ref={boxRef}
      className="relative mx-auto min-h-[124px] max-h-[240px] w-full flex-1"
    >
    <svg
      viewBox={`0 0 ${BOX} ${BOX}`}
      /*
        * Taken out of the flow, so the ring is sized BY its box and never
        * sizes it.
        *
        * In flow it contributed an intrinsic height, and `align-self:
        * stretch` on the column does not cap an item -- it only grows a
        * short one. So the ring grew, the column grew past the tiles
        * beside it, and the page scrolled: the ring was deciding how much
        * room it had. Absolute, it measures what is left instead.
        *
        * `w-full h-full` with the viewBox's default `xMidYMid meet`: the
        * ring is drawn at whichever of the two runs out first and stays
        * circular and centred either way, so the box does not have to be
        * square for the chart to be.
        */
      className="absolute inset-0 block h-full w-full"
      onMouseLeave={() => setHovered(null)}
      role="img"
      aria-label={totalLabel}
      data-testid="overview-categories-donut"
    >
      {/* Rotated so the first and biggest slice starts at twelve o'clock. */}
      <g transform={`rotate(-90 ${BOX / 2} ${BOX / 2})`}>
        {slices.map((slice) => {
          const arc = arcOf(slice, total, cursor);
          cursor = arc.next;
          return (
            <circle
              key={slice.key}
              cx={BOX / 2}
              cy={BOX / 2}
              r={RADIUS}
              fill="none"
              stroke={slice.colour}
              /* The pointed-at wedge thickens outward by a pixel and the
                 rest step back, so the answer in the label is also visible
                 in the ring -- a reader should not have to trust that the
                 words belong to the wedge under the cursor. */
              strokeWidth={hovered === slice.key ? 15 : 13}
              strokeDasharray={arc.dash}
              strokeDashoffset={arc.offset}
              opacity={hovered && hovered !== slice.key ? 0.35 : 1}
              onMouseEnter={() => setHovered(slice.key)}
              onMouseMove={track}
              /*
                * `stopPropagation` as well as `preventDefault`: the card
                * around this ring is a link to the unfiltered list, and
                * without both the browser follows it and lands on the list
                * with the filter thrown away -- the one outcome that looks
                * like the click worked and did the wrong thing.
                */
              onClick={onSelect && ((event) => {
                event.preventDefault();
                event.stopPropagation();
                onSelect(slice);
              })}
              className={onSelect ? 'cursor-pointer' : undefined}
            >
              <title>{`${slice.label}: ${slice.count}`}</title>
            </circle>
          );
        })}
      </g>

      {/*
        * The total sits in the hole rather than above the ring: it is the
        * one number the card leads with, and the ring is what it is made
        * of. Text tokens, never a slice colour -- identity belongs to the
        * mark, and a coloured figure would claim to belong to one slice.
        */}
      <text
        x={BOX / 2}
        y={BOX / 2 - 1}
        textAnchor="middle"
        className="fill-[var(--text-primary)] [font-size:15px] [font-weight:var(--font-bold)] tabular-nums"
      >
        {total}
      </text>
      <text
        x={BOX / 2}
        y={BOX / 2 + 9}
        textAnchor="middle"
        className="fill-[var(--text-tertiary-soft)] [font-size:6px]"
      >
        {totalLabel}
      </text>
    </svg>

      {/*
        * Follows the pointer rather than sitting in a fixed corner: a
        * wedge is a few pixels of arc at the sizes this ring gets, and a
        * label parked away from it would have to be matched back to the
        * wedge by colour -- the exact job this is here to spare the
        * reader.
        *
        * `pointer-events-none` so the label cannot get between the cursor
        * and the wedge it describes and flicker the hover off.
        */}
      {active && (
        <Text component="span"
          role="status"
          data-testid="overview-categories-tooltip"
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-(--radius-sm) bg-[var(--text-primary)] px-(--space-2) py-(--space-0-5) text-(length:--text-tag) text-[var(--surface-raised)] shadow-[var(--shadow-md)]"
          style={{ left: pointer.x, top: pointer.y - 6 }}
        >
          {`${active.label} · ${active.count}`}
        </Text>
      )}
    </div>
  );
}
